import React, {useState} from 'react';
import {Link, useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {AudienceSegment} from "types/AudienceSegment";
import Button from "components/Button";
import {PencilIcon, PlusSmIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteAudienceSegment from "pages/AudienceSegment/Delete";
import {formattedDate} from 'services/date';
import {Exhibit} from 'types/Exhibit';
import {Event} from 'types/Event';
import StackedListItem from 'components/StackedListItem';
import StackedList from 'components/StackedList';
import {
  BadgeCheckIcon,
  CalendarIcon,
  CashIcon, ClipboardListIcon,
  ClockIcon,
  LibraryIcon,
  LocationMarkerIcon,
  LoginIcon,
  TicketIcon,
  UsersIcon
} from "@heroicons/react/outline";
import {DateTime} from "luxon";
import {plural} from "pluralize";
import {useOperator} from "context/OperatorContext";
import {toCamelCase, toTitleCase} from "services/string";
import type {TicketType} from "types/TicketType";
import type {MembershipPlan} from "types/MembershipPlan";
import type {SponsorshipTier} from 'types/SponsorshipTier';
import type {Role} from "types/Role";
import type {Site} from "types/Site";
import type {Attribute} from "types/Attribute";

// Helpers ----

async function fetchSponsorableName(type: string, id: number) {
  const resource = toCamelCase(plural(type)) as keyof Pick<typeof ngage, 'sites' | 'zones' | 'facilities' | 'exhibits' | 'elements'>
  const sponsorable = await ngage[resource].get(id)

  return sponsorable?.name ?? null
}

async function fetchSegment(id: number) {
  const segment = await ngage.audienceSegments.get(id)

  if(!segment) return null

  segment.criteria.groups = await Promise.all(segment.criteria.groups.map(async (group) => {

    if(!!group.filters.sponsorship && !!group.filters.sponsorship.sponsorable_type && !!group.filters.sponsorship.sponsorable_id) {
      group.filters.sponsorship.sponsorable_name = await fetchSponsorableName(
        group.filters.sponsorship.sponsorable_type,
        group.filters.sponsorship.sponsorable_id
      )
    }

    return group
  }))

  return segment
}

function findEntity<T extends { id: number }>(entities: Array<T>, id: string | number): T | null {
  return entities.find(entity => entity.id === Number(id)) ?? null
}

const operatorLabels: Record<string, string> = {
  '<': 'fewer than',
  '<=': 'no more than',
  '=': 'exactly',
  '>=': 'at least',
  '>': 'more than',
}

// Component ----

function ViewAudienceSegment() {
  const {termFor} = useOperator()
  const {id} = useParams()
  const {data: audienceSegment, isLoading} = useQuery(
    ['audience-segments', {id}],
    () => fetchSegment(Number(id))
  )

  const criteria = {page: 1, perPage: 1000};

  // TODO: Include exhibit/event data in the API response, removing the need for this
  const {data: ticketTypes} = useQuery(
    ['ticket-types.all', criteria],
    () => ngage.ticketTypes.all(criteria.page, criteria.perPage)
  )

  const {data: membershipPlans} = useQuery(
    ['membership-plans.all', criteria],
    () => ngage.membershipPlans.all(criteria.page, criteria.perPage)
  )

  const {data: sponsorshipTiers} = useQuery(
    ['sponsorship-tiers.all', criteria],
    () => ngage.sponsorshipTiers.all(criteria.page, criteria.perPage)
  )

  const {data: exhibits} = useQuery(
    ['exhibits.all', criteria],
    () => ngage.exhibits.all({page: criteria.page, per_page: criteria.perPage})
  )

  const {data: sites} = useQuery(
    ['sites.all', criteria],
    () => ngage.sites.filtered({page: criteria.page, per_page: criteria.perPage})
  )

  const {data: events} = useQuery(
    ['events.all', criteria],
    () => ngage.events.all(criteria.page, criteria.perPage)
  )

  const {data: roles} = useQuery(
    ['roles.all'],
    ngage.roles.all
  )

  const {data: attributes} = useQuery(
    ['attributes.all'],
    () => ngage.attributes.filtered(criteria)
  )

  const [deletingAudienceSegment, setDeletingAudienceSegment] = useState<AudienceSegment | null>(null);
  const navigate = useNavigate()

  // Prepare action handlers
  function handleDeleteAudienceSegment() {
    if (audienceSegment) setDeletingAudienceSegment(audienceSegment)
  }

  function handleEditAudienceSegment() {
    if (id) navigate(`/audience-segments/${id}/edit`)
  }

  // Prepare breadcrumbs
  const breadcrumbs = {
    "Audience": "/",
    "Segments": "/audience-segments",
    [audienceSegment?.name || "View Audience Segment"]: `/audience-segments/${id}`
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteAudienceSegment}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditAudienceSegment}>Edit</Button>
  </>

  return (
    <Page
      title={audienceSegment?.name || "View Audience Segment"}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >
      {audienceSegment &&
        <>
          {/* Audience Segment Information */}
          <section>
            <Card
              heading="Audience Segment Information"
              subheading={`A segment with ${audienceSegment.criteria.groups.length} groups`}
            >
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Current Audience</DescriptionTerm>
                  <DescriptionDetails>
                    {audienceSegment.audience_size}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Created</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(audienceSegment.created_at)}
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          {/* Groups */}
          <section className="mt-4">
            <ul>
              {audienceSegment.criteria.groups.map((group, groupId) => (
                <li key={groupId} className="mb-4">
                  <Card heading={`Group ${groupId + 1}`}>
                    <StackedList>
                      {!!group.filters.location &&
                        <StackedListItem icon={LocationMarkerIcon}>
                          <span>Current location is</span>{' '}
                          <span>{group.filters.location.mode}</span>{' '}

                          {group.filters.location.sites.length < 1 &&
                            <span>{group.filters.location.mode === 'on'? `any ${termFor('site')}` : `all ${termFor('sites')}`}</span>
                          }

                          {group.filters.location.sites.length >= 1 &&
                            <>
                              <span>{`these ${termFor('sites')}:`}</span>

                              <ul className="mt-1 ml-2">
                                {group.filters.location.sites.map((siteFilter, siteIndex) => (
                                  <li key={siteIndex}>
                                    <Link
                                      to={`/sites/${siteFilter.site_id}`}
                                      className="font-semibold text-brand border-b border-dashed border-brand">
                                      {sites ? findEntity<Site>(sites.data, siteFilter.site_id)?.name : 'Unknown site'}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </>
                          }
                        </StackedListItem>
                      }

                      {!!group.filters.visit &&
                        <StackedListItem icon={ClockIcon}>
                          <span>Visited:</span>{' '}

                          <ul className="mt-1 ml-2">
                            {group.filters.visit.sites.map((siteVisitFilter, siteVisitIndex) => (
                              <li key={siteVisitIndex}>
                                {!!siteVisitFilter.site_id &&
                                  <Link
                                    to={`/sites/${siteVisitFilter.site_id}`}
                                    className="font-semibold text-brand border-b border-dashed border-brand">
                                    {sites ? findEntity<Site>(sites.data, siteVisitFilter.site_id)?.name : 'Unknown site'}
                                  </Link>
                                }

                                {!siteVisitFilter.site_id &&
                                  <span>Any site</span>
                                }

                                {' '}

                                <span>{operatorLabels[siteVisitFilter.operator]} {siteVisitFilter.count === 1 ? 'once' : `${siteVisitFilter.count} times`}</span>{' '}

                                {siteVisitFilter.mode === 'between' &&
                                  <span>between {formattedDate(siteVisitFilter.dates[0])} and {formattedDate(siteVisitFilter.dates[1])}</span>
                                }

                                {siteVisitFilter.mode === 'before' &&
                                  <span>before {formattedDate(siteVisitFilter.dates[0])}</span>
                                }

                                {siteVisitFilter.mode === 'after' &&
                                  <span>after {formattedDate(siteVisitFilter.dates[0])}</span>
                                }

                                {siteVisitFilter.mode === 'ever' &&
                                  <span>ever</span>
                                }
                              </li>
                            ))}
                          </ul>
                        </StackedListItem>
                      }

                      {!!group.filters.interaction &&
                        <StackedListItem icon={LibraryIcon}>
                          <span>Interacted with:</span>{' '}

                          <ul className="mt-1 ml-2">
                            {group.filters.interaction.exhibits.map((exhibitFilter, exhibitIndex) => (
                              <li key={exhibitIndex}>
                                <Link
                                  to={`/exhibits/${exhibitFilter.exhibit_id}`}
                                  className="font-semibold text-brand border-b border-dashed border-brand">
                                  {exhibits ? findEntity<Exhibit>(exhibits.data, exhibitFilter.exhibit_id)?.name : 'Unknown exhibit'}
                                </Link>{' '}

                                {exhibitFilter.mode === 'between' &&
                                  <span>between {formattedDate(exhibitFilter.dates[0])} and {formattedDate(exhibitFilter.dates[1])}</span>
                                }

                                {exhibitFilter.mode === 'before' &&
                                  <span>before {formattedDate(exhibitFilter.dates[0])}</span>
                                }

                                {exhibitFilter.mode === 'after' &&
                                  <span>after {formattedDate(exhibitFilter.dates[0])}</span>
                                }

                                {exhibitFilter.mode === 'ever' &&
                                  <span>ever</span>
                                }

                                {exhibitIndex < ((group.filters.interaction?.exhibits.length || 0) - 1) ?
                                  <PlusSmIcon className="ml-1 h-5 w-5 text-gray-500 inline align-bottom"/> : null}
                              </li>
                            ))}
                          </ul>
                        </StackedListItem>
                      }

                      {!!group.filters.attendance &&
                        <StackedListItem icon={CalendarIcon}>
                          <span>Interacted with:</span>{' '}

                          <ul className="mt-1 ml-2">
                            {group.filters.attendance.events.map((eventFilter, eventIndex) => {
                              let thisEvent = events ? findEntity<Event>(events.data, eventFilter.event_id) : null

                              return (
                                <li key={eventIndex}>
                                  <Link
                                    to={`/events/${eventFilter.event_id}`}
                                    className="font-semibold text-brand border-b border-dashed border-brand">
                                    {thisEvent ? thisEvent.name : `Unknown ${termFor('event')}`}
                                  </Link>
                                  {' '}
                                  ({thisEvent ? thisEvent.starts_at.toLocaleString(DateTime.DATETIME_MED) : null})
                                  {eventIndex < ((group.filters.attendance?.events.length || 0) - 1) ?
                                    <PlusSmIcon className="ml-1 h-5 w-5 text-gray-500 inline align-bottom"/> : null}
                                </li>
                              )
                            })}
                          </ul>
                        </StackedListItem>
                      }

                      {!!group.filters.ticket &&
                        <StackedListItem icon={TicketIcon}>
                          <span>{group.filters.ticket.not ? "Doesn't hold" : "Holds"}</span>
                          {' '}
                          <span
                            className="font-semibold">{findEntity<TicketType>(ticketTypes?.data ?? [], group.filters.ticket.type_id)?.name || "any"}</span>
                          {' '}
                          <span>ticket, </span>
                          {' '}
                          <span
                            className="font-semibold">{group.filters.ticket.status === "any" ? "in any status" : `${group.filters.ticket.status}`}</span>
                          {' '}
                          <span
                            className="font-semibold">{group.filters.ticket.mode === "ever" ? "at any time" : group.filters.ticket.mode.replace('-', ' ')}</span>
                          {' '}
                          <span className="font-semibold">{formattedDate(group.filters.ticket.dates[0])}</span>

                          {group.filters.ticket.mode === 'between' &&
                            <>
                              {' '}
                              <span>and</span>
                              {' '}
                              <span className="font-semibold">{formattedDate(group.filters.ticket.dates[1])}</span>
                            </>

                          }
                        </StackedListItem>
                      }

                      {!!group.filters.membership &&
                        <StackedListItem icon={BadgeCheckIcon}>
                          <span>{group.filters.membership.not ? "Doesn't hold" : "Holds"}</span>
                          {' '}
                          <span
                            className="font-semibold">{findEntity<MembershipPlan>(membershipPlans?.data ?? [], group.filters.membership.plan_id)?.name || "any"}</span>
                          {' '}
                          <span>membership that</span>
                          {' '}
                          <span
                            className="font-semibold">{group.filters.membership.status === "any" ? "has any status" : `is ${group.filters.membership.status}`}</span>
                          {' '}
                          <span
                            className="font-semibold">{group.filters.membership.mode === "ever" ? "at any time" : group.filters.membership.mode}</span>
                          {' '}
                          <span className="font-semibold">{formattedDate(group.filters.membership.dates[0])}</span>

                          {group.filters.membership.mode === 'between' &&
                            <>
                              {' '}
                              <span>and</span>
                              {' '}
                              <span className="font-semibold">{formattedDate(group.filters.membership.dates[1])}</span>
                            </>

                          }
                        </StackedListItem>
                      }

                      {!!group.filters.sponsorship &&
                        <StackedListItem icon={CashIcon}>
                          <span>{group.filters.sponsorship.not ? "Doesn't have" : "Has"}</span>
                          {' '}
                          <span
                            className="font-semibold">{findEntity<SponsorshipTier>(sponsorshipTiers?.data ?? [], group.filters.sponsorship.tier_id)?.name || "any"}</span>
                          {' '}
                          <span>{termFor('sponsorship')} for</span>
                          {' '}
                          <span className="font-semibold">{group.filters.sponsorship.sponsorable_name ?? "any sponsorable"}</span>
                          {' '}
                          <span
                            className="font-semibold">{group.filters.sponsorship.status === "any" ? "with any status" : `${group.filters.sponsorship.status}`}</span>
                          {' '}
                          <span
                            className="font-semibold">{group.filters.sponsorship.mode === "ever" ? "at any time" : group.filters.sponsorship.mode.replace('-', ' ')}</span>
                          {' '}
                          {['on', 'before', 'after', 'between'].includes(group.filters.sponsorship.mode) &&
                            <span className="font-semibold">{formattedDate(group.filters.sponsorship.dates[0])}</span>
                          }

                          {group.filters.sponsorship.mode === 'between' &&
                            <>
                              {' '}
                              <span>and</span>
                              {' '}
                              <span className="font-semibold">{formattedDate(group.filters.sponsorship.dates[1])}</span>
                            </>

                          }
                        </StackedListItem>
                      }

                      {!!group.filters.login &&
                        <StackedListItem icon={LoginIcon}>
                          <span>First logged-in</span>
                          {' '}
                          <span>{group.filters.login.mode}</span>
                          {' '}
                          <span>{formattedDate(group.filters.login.dates[0])}</span>

                          {group.filters.login.mode === 'between' &&
                            <>
                              {' '}
                              <span>and</span>
                              {' '}
                              <span>{formattedDate(group.filters.login.dates[1])}</span>
                            </>

                          }
                        </StackedListItem>
                      }

                      {!!group.filters.role &&
                        <StackedListItem icon={UsersIcon}>
                          <span>Has any of the following roles:</span>{' '}

                          <ul className="mt-1 ml-2">
                            {group.filters.role.value.map(roleId => {
                              const role = roles ? findEntity<Role>(roles, roleId) : null

                              return (!!role &&
                                <li key={role.id}>
                                  {toTitleCase(role.name)}
                                </li>
                              )
                            })}
                          </ul>
                        </StackedListItem>
                      }

                      {!!group.filters.attribute &&
                        <StackedListItem icon={ClipboardListIcon}>
                          <span>Has all of the following attributes:</span>{' '}

                          <ul className="mt-1 ml-2">
                            {group.filters.attribute.matches.map((match, index) => {
                              const attribute = attributes? findEntity<Attribute>(attributes.data, match.attribute_id) : null;

                              if(!attribute) return null

                              let value;

                              switch(attribute.type){
                                case 'boolean':
                                  value = match.value? 'Yes' : 'No';
                                  break;

                                case 'date':
                                  value = Array.isArray(match.value)? match.value.join(' and ') : match.value;
                                  break;

                                default:
                                  value = match.value;
                              }

                              return (
                                <li key={`${match.attribute_id}-${index}`}>
                                  <span className="font-semibold">{toTitleCase(attribute.name)}</span>
                                  {' ('}

                                  {!!match.mode &&
                                    <>
                                      <span>where value
                                        {' '}
                                        {['not', 'between', 'on', 'before', 'after'].includes(match.mode)? 'is ' : ''}
                                        {match.mode.replaceAll('-', ' ')}
                                      </span>
                                      {' '}
                                      <span className="font-semibold">{value}</span>
                                    </>
                                  }

                                  {!match.mode &&
                                    <span>with any value</span>
                                  }

                                  {')'}
                                </li>
                              );
                            })}
                          </ul>
                        </StackedListItem>
                      }
                    </StackedList>
                  </Card>
                </li>
              ))}
            </ul>
          </section>
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteAudienceSegment
        show={audienceSegment === deletingAudienceSegment}
        audienceSegment={deletingAudienceSegment}
        onCancel={() => setDeletingAudienceSegment(null)}
        onDelete={() => navigate('/audience-segments')}
        onDeleteFail={() => setDeletingAudienceSegment(null)}
      />
    </Page>
  );
}

export default ViewAudienceSegment;